import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModeButtons } from '@/components/Toolbar/ModeButtons'
import { ZoomControls } from '@/components/Toolbar/ZoomControls'
import { Button } from '@/components/ui/Button'
import {
  AnimationIcon,
  DefaultsIcon,
  ExportIcon,
  EyeIcon,
  GridIcon,
  GroupsIcon,
  HelpIcon,
  HistoryIcon,
  ImportIcon,
  RelationshipIcon,
  TableIcon,
  ShortcutsIcon,
} from '@/components/ui/icons'
import { Tooltip, TOOLBAR_TOOLTIP_DELAY_MS } from '@/components/ui/Tooltip'
import type { PresenceUser } from '@/stores/presenceStore'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { useModalStore } from '@/hooks/useModalStore'
import { useSelectionStore, useSettingsStore } from '@/stores'
import { usePresenceStore } from '@/stores/presenceStore'
import { useDraftStore } from '@/stores/draftStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useProjectCan } from '@/hooks/useProjectCan'
import { morphToast } from '@/components/ui/Toast'

export function ProjectHeader() {
  const { t } = useTranslation()
  const openCreate = useTableEditorModal((state) => state.openCreate)
  const openDefaults = useModalStore((state) => state.openDefaults)
  const openGroups = useModalStore((state) => state.openGroups)
  const openVersions = useModalStore((state) => state.openVersions)
  const openExport = useModalStore((state) => state.openExport)
  const openSqlImport = useModalStore((state) => state.openSqlImport)
  const hasActiveProject = Boolean(useTabsStore((state) => state.activeTabId))
  const readOnly = useDraftStore((state) => state.mode === 'view')
  const relMode = useSelectionStore((state) => state.relMode)
  const startRelationshipCreation = useSelectionStore((state) => state.startRelationshipCreation)
  const cancelRelationshipCreation = useSelectionStore((state) => state.cancelRelationshipCreation)
  const openShortcuts = useModalStore((state) => state.openShortcuts)
  const openCanvasGuide = useModalStore((state) => state.openCanvasGuide)
  const showGrid = useSettingsStore((s) => s.showGrid)
  const toggleGrid = useSettingsStore((s) => s.toggleGrid)
  const relationshipAnimationEnabled = useSettingsStore((s) => s.relationshipAnimationEnabled)
  const toggleRelationshipAnimation = useSettingsStore((s) => s.toggleRelationshipAnimation)
  const canEdit = useProjectCan('edit')
  const presenceUsers = Object.values(usePresenceStore((s) => s.users))

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 shrink-0 min-h-[44px] flex-wrap"
      style={{ background: 'var(--bg-toolbar)', borderBottom: '1px solid var(--border)' }}
    >
      <ModeButtons />
      <Divider />

      <IconBtn
        dataGuide="table-button"
        title={t('toolbar.table')}
        onClick={() => openCreate()}
        disabled={!hasActiveProject || !canEdit || readOnly}
        icon={<TableIcon />}
      />
      <IconBtn
        dataGuide="relationship-button"
        title={relMode ? t('relation.cancel', 'Cancelar relação') : t('toolbar.relationship')}
        onClick={() => {
          if (relMode) {
            cancelRelationshipCreation()
            return
          }
          startRelationshipCreation()
          morphToast(t('relation.stepSource'))
        }}
        active={relMode}
        disabled={!hasActiveProject || !canEdit || readOnly}
        icon={<RelationshipIcon />}
      />

      <Divider />

      <IconBtn
        dataGuide="defaults-button"
        title={t('settingsMenu.defaults')}
        onClick={openDefaults}
        disabled={!hasActiveProject || readOnly}
        icon={<DefaultsIcon />}
      />
      <IconBtn
        dataGuide="groups-button"
        title={t('settingsMenu.groups')}
        onClick={openGroups}
        disabled={!hasActiveProject || readOnly}
        icon={<GroupsIcon />}
      />

      <Divider />

      <IconBtn
        dataGuide="versions-button"
        title={t('projectMenu.versions')}
        onClick={openVersions}
        disabled={!hasActiveProject}
        icon={<HistoryIcon />}
      />

      <Divider />

      <IconBtn
        dataGuide="export-button"
        title={t('projectMenu.export')}
        onClick={openExport}
        disabled={!hasActiveProject}
        icon={<ExportIcon />}
      />
      <IconBtn
        dataGuide="import-button"
        title={t('projectMenu.import')}
        onClick={() => openSqlImport('editor-import')}
        disabled={readOnly}
        icon={<ImportIcon />}
      />

      <Divider />

      <div data-guide="view-controls" className="flex items-center gap-2">
        <IconBtn
          title={t('settingsMenu.grid')}
          onClick={() => {
            toggleGrid()
            morphToast.success(t(showGrid ? 'prompts.gridOff' : 'prompts.gridOn'))
          }}
          active={showGrid}
          icon={<GridIcon />}
        />
        <IconBtn
          title={t('settingsMenu.animation')}
          onClick={() => {
            toggleRelationshipAnimation()
            morphToast.success(
              t(relationshipAnimationEnabled ? 'prompts.relAnimationOff' : 'prompts.relAnimationOn'),
            )
          }}
          active={relationshipAnimationEnabled}
          icon={<AnimationIcon />}
        />
      </div>
      <IconBtn dataGuide="shortcuts-button" title={t('settingsMenu.shortcuts')} onClick={openShortcuts} icon={<ShortcutsIcon />} />
      <IconBtn dataGuide="help-button" title={t('toolbar.help')} onClick={openCanvasGuide} icon={<HelpIcon />} />

      <div className="ml-auto flex items-center gap-2">
        {readOnly && (
          <div
            className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium"
            style={{
              borderColor: 'var(--color-warning-border)',
              color: 'var(--color-warning)',
              background: 'var(--color-warning-bg)',
            }}
          >
            <EyeIcon />
            {t('header.viewOnly', 'Somente visualização')}
          </div>
        )}
        {presenceUsers.length > 0 && (
          <PresenceAvatars users={presenceUsers} />
        )}
        <ZoomControls />
      </div>
    </div>
  )
}

function Divider() {
  return <div className="h-6 w-px" style={{ background: 'var(--border)' }} />
}

const PRESENCE_MAX = 5

function PresenceAvatars({ users }: { users: PresenceUser[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const visible = users.slice(0, PRESENCE_MAX)
  const extra = users.length - PRESENCE_MAX

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center rounded-full px-1.5 py-0.5 transition-colors"
        style={{
          border: 'none',
          background: 'transparent',
        }}
      >
        <div className="flex items-center">
          {visible.map((user, i) => (
            <Tooltip key={user.userId} content={user.editing?.tableId ? `${user.name} — editando ${user.editing.tableId}` : user.name} delayMs={200}>
              <div
                style={{
                  marginLeft: i === 0 ? 0 : -7,
                  zIndex: visible.length - i,
                  position: 'relative',
                  boxShadow: '0 0 0 2px var(--bg-toolbar)',
                  borderRadius: '50%',
                }}
              >
                <UserAvatar userId={user.userId} name={user.name} email={user.email ?? ''} size={24} color={user.color} />
              </div>
            </Tooltip>
          ))}
          {extra > 0 && (
            <div
              className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold"
              style={{
                marginLeft: -7,
                zIndex: 0,
                position: 'relative',
                background: 'var(--border)',
                color: 'var(--text-muted)',
                boxShadow: '0 0 0 2px var(--bg-toolbar)',
              }}
            >
              +{extra}
            </div>
          )}
        </div>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 min-w-[200px] rounded-xl border py-1.5 shadow-xl"
          style={{ background: 'var(--bg-popup)', borderColor: 'var(--border)' }}
        >
          <div className="px-3 pb-1 pt-0.5 text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-dim)' }}>
            {t('header.onlineUsers', 'Online agora')}
          </div>
          {users.map((user) => (
            <div key={user.userId} className="flex items-center gap-2.5 px-3 py-1.5">
              <UserAvatar userId={user.userId} name={user.name} email={user.email ?? ''} size={28} color={user.color} />
              <div className="min-w-0">
                <div className="truncate text-[12px]" style={{ color: 'var(--text)' }}>{user.name}</div>
                {user.editing?.tableId && (
                  <div className="truncate text-[10px]" style={{ color: 'var(--text-dim)' }}>
                    {t('header.editing', 'editando')} {user.editing.tableId}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface IconBtnProps {
  dataGuide?: string
  title: string
  onClick: () => void
  icon: ReactNode
  disabled?: boolean
  active?: boolean
  variant?: 'secondary' | 'primary'
}

function IconBtn({ dataGuide, title, onClick, icon, disabled, active, variant = 'secondary' }: IconBtnProps) {
  const primaryStyle = variant === 'primary' && !disabled
    ? {
        background: 'var(--accent)',
        borderColor: 'var(--accent)',
        color: '#fff',
      }
    : undefined
  const activeStyle = active
    ? {
        borderColor: 'var(--accent)',
        background: 'rgba(99,102,241,.14)',
        color: 'var(--accent)',
      }
    : undefined
  // Sem `title`: o tooltip nativo do navegador leva mais de um segundo para
  // aparecer, o que numa barra de ferramentas parece que nada aconteceu.
  return (
    <Tooltip content={title} delayMs={TOOLBAR_TOOLTIP_DELAY_MS}>
      <Button
        data-guide={dataGuide}
        variant="icon"
        size="sm"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        icon={icon}
        style={{ ...primaryStyle, ...activeStyle }}
      />
    </Tooltip>
  )
}
